import { useEffect, useState } from 'react';
import { MgmtApi } from '@/customApiClients/mgmtApiClient/Api';
import { FONT_CACHE_KEY, FONT_SIZE_CACHE_KEY } from '@/initFont';

// Google Fonts that need to be loaded
const GOOGLE_FONTS = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Nunito',
    'PT Sans',
    'Raleway'
];

const loadGoogleFont = (fontName: string) => {
    if (!GOOGLE_FONTS.includes(fontName)) return;

    // Check if font is already loaded
    const existingLink = document.querySelector(`link[href*="${fontName.replace(/\s/g, '+')}"]`);
    if (existingLink) return;

    // Create and append link tag
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s/g, '+')}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
};

export const useOrganizationFont = () => {
    const [font, setFont] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchFont = async () => {
            try {
                const mgmtApi = new MgmtApi({
                    baseUrl: '/mgmt',
                    baseApiParams: { credentials: 'include' }
                });

                const response = await mgmtApi.api.v1OrganizationsFontsList();
                const fontData = response?.data?.data?.[0];

                if (fontData?.font) {
                    setFont(fontData.font);

                    // Cache font in localStorage
                    localStorage.setItem(FONT_CACHE_KEY, fontData.font);
                    if (fontData.fontSize) {
                        localStorage.setItem(FONT_SIZE_CACHE_KEY, fontData.fontSize);
                    }

                    // Load Google Font if needed
                    loadGoogleFont(fontData.font);

                    // Update CSS variable
                    document.documentElement.style.setProperty('--font-sans', `'${fontData.font}', sans-serif`);

                    // Update font size
                    if (fontData.fontSize) {
                        document.documentElement.style.fontSize = fontData.fontSize;
                    }
                }
            } catch (error) {
                console.error('Failed to load organization font:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFont();
    }, []);

    return { font, loading };
};
